import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/Toronto';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('stellar_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('stellar_token');
    localStorage.removeItem('stellar_user');
    setUser(null);
    setToken(null);
  }, []);

  useEffect(() => {
    if (token) {
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          setLoading(false);
          // RF-A2 — first-login-only timezone autodetect. Never overwrite
          // a saved timezone silently. This useEffect runs on every mount
          // when a token exists, so the bug was that prior code clobbered
          // the user's chosen tz on every reload. Match login() paths.
          const browserTz = detectTimezone();
          const userTz = res.data?.timezone;
          if (!userTz && browserTz) {
            authAPI.updateProfile({ timezone: browserTz }).catch(() => {});
          }
        })
        .catch(() => {
          logout();
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = (tokenOrEmail, userDataOrPassword) => {
    if (typeof tokenOrEmail === 'string' && tokenOrEmail.startsWith('ey')) {
      localStorage.setItem('stellar_token', tokenOrEmail);
      localStorage.setItem('stellar_user', JSON.stringify(userDataOrPassword));
      setToken(tokenOrEmail);
      setUser(userDataOrPassword);
      // Auto-sync timezone on login
      const browserTz = detectTimezone();
      // RF-A2 — only autodetect on FIRST login (when timezone is NULL).
      // Never silently overwrite a user's chosen timezone.
      if (userDataOrPassword && !userDataOrPassword.timezone && browserTz) {
        authAPI.updateProfile({ timezone: browserTz }).catch(() => {});
      }
      return;
    }
    return authAPI.login({ email: tokenOrEmail, password: userDataOrPassword })
      .then(res => {
        const { token: newToken, user: userData, workspaces, must_change_password } = res.data;
        localStorage.setItem('stellar_token', newToken);
        localStorage.setItem('stellar_user', JSON.stringify(userData));
        setToken(newToken);
        setUser(userData);
        // RF-A2 — same first-login-only rule for the password-login path.
        const browserTz = detectTimezone();
        if (!userData.timezone && browserTz) {
          authAPI.updateProfile({ timezone: browserTz }).catch(() => {});
        }
        return { user: userData, workspaces, must_change_password };
      });
  };

  const register = async (name, email, password) => {
    const browserTz = detectTimezone();
    const res = await authAPI.register({ name, email, password, timezone: browserTz });
    if (res.data.status === 'pending') {
      return { status: 'pending', message: res.data.message };
    }
    const { token: newToken, user: userData, workspace } = res.data;
    localStorage.setItem('stellar_token', newToken);
    localStorage.setItem('stellar_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    return { user: userData, workspace };
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      localStorage.setItem('stellar_user', JSON.stringify(res.data));
    } catch {}
  }, []);

  // Heartbeat for idle detection — ping every 60s while active
  useEffect(() => {
    if (!token || !user) return;
    let lastActivity = Date.now();
    const onActivity = () => { lastActivity = Date.now(); };
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    const interval = setInterval(() => {
      // Only send heartbeat if there was activity in the last 90 seconds
      if (Date.now() - lastActivity < 90000) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity/heartbeat`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => {});
      }
    }, 60000);
    // Send initial heartbeat
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity/heartbeat`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    }).catch(() => {});
    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, [token, user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
